/**
 * LoRA WebSocket event consumer (Phase 06).
 *
 * Listens for the three LoRA event types emitted by orchestrator-alpha
 * (Phase 05) on the `'lora'` channel and invalidates the matching
 * TanStack Query caches so the UI updates in realtime without polling.
 *
 * Mount once at the app root alongside the existing event consumers.
 *
 * Event payload shapes (orchestrator-alpha/src/websocket/events/lora.types.ts):
 * - lora.training.status_changed:
 *     { loraId, trainingRunId, status, externalJobId?, errorMessage? }
 * - lora.checkpoint.added:
 *     { loraId, trainingRunId, checkpoint: { id, stepNumber, statusSlug, ... },
 *       wasFirstForLora }
 * - lora.status_changed:
 *     { loraId, status, defaultCheckpointId? }
 */

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { loraQueryKeys, trainingRunQueryKeys } from "./queryKeys";

type AddMessageListener = (listener: (message: unknown) => void) => () => void;

interface LoraTrainingStatusEvent {
  loraId: string;
  trainingRunId: string;
  status: string;
  externalJobId?: string | null;
  errorMessage?: string | null;
}

interface LoraCheckpointAddedEvent {
  loraId: string;
  trainingRunId: string;
  checkpoint: { id: string; stepNumber: number; statusSlug: string };
  wasFirstForLora?: boolean;
}

interface LoraStatusChangedEvent {
  loraId: string;
  status: string;
  defaultCheckpointId?: string | null;
}

interface InboundMessage {
  eventType?: string;
  channel?: string;
  data?: unknown;
  eventData?: unknown;
}

export function useLoraEvents(
  addMessageListener: AddMessageListener | undefined,
) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!addMessageListener) return;

    return addMessageListener((raw) => {
      const message = raw as InboundMessage;
      const eventType = message?.eventType;
      if (typeof eventType !== "string") return;

      const payload = (message.data ?? message.eventData) as
        | LoraTrainingStatusEvent
        | LoraCheckpointAddedEvent
        | LoraStatusChangedEvent
        | undefined;
      if (!payload) return;

      switch (eventType) {
        case "lora.training.status_changed": {
          const ev = payload as LoraTrainingStatusEvent;
          queryClient.invalidateQueries({
            queryKey: trainingRunQueryKeys.detail(ev.trainingRunId),
          });
          queryClient.invalidateQueries({
            queryKey: loraQueryKeys.trainingRuns(ev.loraId),
          });
          queryClient.invalidateQueries({
            queryKey: loraQueryKeys.detail(ev.loraId),
          });
          return;
        }
        case "lora.checkpoint.added": {
          const ev = payload as LoraCheckpointAddedEvent;
          queryClient.invalidateQueries({
            queryKey: loraQueryKeys.checkpoints(ev.loraId),
          });
          queryClient.invalidateQueries({
            queryKey: trainingRunQueryKeys.detail(ev.trainingRunId),
          });
          if (ev.wasFirstForLora) {
            queryClient.invalidateQueries({
              queryKey: loraQueryKeys.detail(ev.loraId),
            });
            queryClient.invalidateQueries({
              queryKey: loraQueryKeys.lists(),
            });
          }
          return;
        }
        case "lora.status_changed": {
          const ev = payload as LoraStatusChangedEvent;
          queryClient.invalidateQueries({
            queryKey: loraQueryKeys.detail(ev.loraId),
          });
          queryClient.invalidateQueries({
            queryKey: loraQueryKeys.lists(),
          });
          return;
        }
        default:
          return;
      }
    });
  }, [addMessageListener, queryClient]);
}
